import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Compass, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <Card className="w-full max-w-md p-8 rounded-2xl border-border">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Compass className="w-4 h-4" /></div>
          <div className="font-display text-lg">TravelVault</div>
        </div>
        {!token ? (
          <div>
            <h1 className="font-display text-2xl font-medium">Invalid link</h1>
            <p className="text-sm text-stone-500 mt-2">This reset link is missing or malformed.</p>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-3"><ShieldCheck className="w-6 h-6" /></div>
            <h1 className="font-display text-2xl font-medium">Password updated</h1>
            <p className="text-sm text-stone-500 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-medium" data-testid="reset-title">Set a new password</h1>
            <p className="text-sm text-stone-500 mt-1">Pick something strong you'll remember on the road.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6}
                       className="rounded-xl mt-1" placeholder="At least 6 characters" data-testid="new-password-input" />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm new password</Label>
                <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={6}
                       className="rounded-xl mt-1" placeholder="Repeat it" data-testid="confirm-password-input" />
              </div>
              <Button type="submit" disabled={busy}
                      className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-transform"
                      data-testid="reset-submit-btn">
                {busy ? "Updating..." : "Update password"}
              </Button>
            </form>
          </>
        )}
        <Link to="/login" className="mt-6 flex items-center gap-1 text-sm text-stone-600 hover:text-primary" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </Card>
    </div>
  );
}
